import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore, selectUser, canAccessMenuSite } from "@safers/auth";
import { Building, ChevronRight } from "@safers/ui/atoms";
import {
  areaOf,
  areaPath,
  cctvLivePath,
  cctvPlaybackPath,
  controlPath,
  healthRecordsPath,
  isPlayback,
} from "@/routes/paths";
import { useIsMobile } from "@/hooks/useIsMobile";
import { controlService } from "@safers/api";
import { controlKeys } from "@/pages/control/useControlData";
import { SEVERITY_COLORS, SEVERITY_TEXT_COLORS, SEVERITY_LABELS } from "@/lib/map";

/**
 * 현장 전환. 관제 헤더(header, 원래 모양)와 보건 조치 기록 제목(title) 두 자리에 놓인다.
 */
export function SiteSwitcher({
  currentId,
  currentName,
  tone = "light",
  placement = "header",
}: {
  currentId: number | null;
  currentName?: string;
  tone?: "light" | "dark";
  placement?: "header" | "title";
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(selectUser);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const area = areaOf(location.pathname);

  const targetPath = (siteId: number): string => {
    if (area === "cctv" && canAccessMenuSite(user, siteId, "CCTV")) {
      return isPlayback(location.pathname) ? cctvPlaybackPath() : cctvLivePath({ siteId });
    }
    if (area === "health" && canAccessMenuSite(user, siteId, "HEALTH")) {
      return healthRecordsPath(siteId);
    }
    return controlPath(siteId);
  };

  const { data } = useQuery({
    queryKey: controlKeys.national(),
    queryFn: () => controlService.getAllSites(),
    staleTime: 60_000,
  });
  const status = data?.siteStatus ?? null;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const groups = status?.regions.filter((r) => r.sites.length > 0) ?? [];
  const total = groups.reduce((n, r) => n + r.sites.length, 0);
  const dark = tone === "dark";
  const isTitle = placement === "title";

  const allSitesPath =
    !isMobile && (area === "control" || area === "health") ? areaPath(area) : null;

  const label =
    currentId == null
      ? "전체 현장"
      : currentName ||
        groups.flatMap((r) => r.sites).find((s) => s.siteId === currentId)?.siteName ||
        "현장 선택";

  if (total <= 1) {
    return isTitle ? (
      <h1 className="mt-0.5 truncate text-xl leading-tight font-bold text-neutral-900">{label}</h1>
    ) : (
      <span
        className={`truncate text-[0.9375rem] font-semibold ${
          dark ? "text-white/70" : "text-neutral-600"
        }`}
      >
        {label}
      </span>
    );
  }

  return (
    <div ref={rootRef} className={isTitle ? "relative mt-0.5 min-w-0" : "relative min-w-0 flex-1"}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="현장 전환"
        className={
          isTitle
            ? "mx-auto flex max-w-full items-center gap-1.5 rounded-lg px-2 py-0.5 transition-colors hover:bg-neutral-200/60"
            : `flex w-full items-center gap-1 rounded-lg px-1.5 py-1 text-left transition-colors sm:gap-1.5 sm:px-2 ${
                dark ? "hover:bg-white/10" : "hover:bg-neutral-100"
              }`
        }
      >
        {isTitle ? (
          <h1 className="min-w-0 truncate text-xl leading-tight font-bold text-neutral-900">
            {label}
          </h1>
        ) : (
          <span
            className={`min-w-0 flex-1 truncate text-[0.9375rem] font-semibold ${
              dark ? "text-white/70" : "text-neutral-600"
            }`}
          >
            {label}
          </span>
        )}
        <svg
          className={`flex-none transition-transform ${open ? "rotate-180" : ""} ${
            isTitle
              ? "h-5 w-5 text-neutral-400"
              : `h-4 w-4 ${dark ? "text-white/40" : "text-neutral-400"}`
          }`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className={`scrollbar-visible absolute top-full z-[900] mt-1 max-h-[60vh] w-[16rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-xl ${
            isTitle ? "left-1/2 -translate-x-1/2 text-left" : "left-0"
          }`}
        >
          {allSitesPath && currentId != null && (
            <div className="sticky top-0 z-10 border-b-2 border-neutral-200 bg-neutral-100 p-1.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(allSitesPath);
                }}
                className="flex w-full items-center gap-2.5 rounded-lg bg-white px-2.5 py-2 text-left ring-1 ring-neutral-200 transition-colors hover:bg-brand/5 hover:ring-brand/50"
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-brand/10 text-brand">
                  <Building size="md" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.9375rem] font-semibold text-neutral-900">
                    전체 현장
                  </span>
                  <span className="block text-[0.6875rem] text-neutral-500">
                    현장 {total}개를 한 화면에서
                  </span>
                </span>
                <ChevronRight size="sm" className="flex-none text-neutral-400" />
              </button>
            </div>
          )}
          {groups.map((g) => (
            <div key={g.region ?? g.displayName}>
              <div className="bg-neutral-50 px-3 py-1.5 text-[0.6875rem] font-bold text-neutral-500">
                {g.displayName}
              </div>
              {g.sites.map((site) => (
                <button
                  key={site.siteId}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(targetPath(site.siteId));
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-neutral-50 ${
                    site.siteId === currentId ? "bg-brand/5" : ""
                  }`}
                >
                  <span
                    className="h-2 w-2 flex-none rounded-full"
                    style={{ background: SEVERITY_COLORS[site.severity] }}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1 truncate text-[0.9375rem] text-neutral-800">
                    {site.siteName}
                  </span>
                  <span
                    className="flex-none text-[0.6875rem] font-semibold"
                    style={{ color: SEVERITY_TEXT_COLORS[site.severity] }}
                  >
                    {SEVERITY_LABELS[site.severity]}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
